import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Typography, Alert, CircularProgress } from '@mui/material';
import { UploadPhotosTab } from '../../profile/uploadPhotosTab';
import { ProvidersService } from '../../../services/ProvidersService';

const PhotosStep = ({ provider, generatedImageId, onNext, onBack, onSkip }) => {
  const [waiting, setWaiting] = useState(false);
  const [genError, setGenError] = useState('');
  const [imageRefreshKey, setImageRefreshKey] = useState(0);

  // Auto-poll for generated image when generatedImageId is provided
  useEffect(() => {
    let cancelled = false;
    const waitForImage = async () => {
      if (!generatedImageId || !provider?.id) return;
      if (provider?.imageList?.includes(generatedImageId)) return; // already landed
      setWaiting(true);
      setGenError('');
      let found = false;
      for (let i = 0; i < 20 && !found && !cancelled; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        try {
          const refreshed = await ProvidersService.getSingleProvider(provider.id);
          if (refreshed?.imageList?.includes(generatedImageId)) found = true;
        } catch (e) { /* keep polling */ }
      }
      if (cancelled) return;
      setWaiting(false);
      setImageRefreshKey((k) => k + 1);
      if (!found) {
        setGenError('Your cover image is still being generated. It will appear here shortly — you can continue and check later.');
      }
    };
    waitForImage();
    return () => { cancelled = true; };
  }, [generatedImageId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box sx={{ maxWidth: '600px', margin: '0 auto' }}>
      <Typography variant="h4" sx={{ marginBottom: '20px' }}>
        Photos
      </Typography>

      {/* AI-generated image placeholder section (show only while waiting or on error) */}
      {(waiting || genError) && (
        <Box
          sx={{
            border: '1px dashed #ccc',
            borderRadius: '4px',
            padding: '24px',
            textAlign: 'center',
            marginBottom: '30px',
            backgroundColor: '#fafafa'
          }}
        >
          <Typography variant="subtitle1" sx={{ marginBottom: '8px', fontWeight: 600 }}>
            AI-generated cover image
          </Typography>
          {waiting && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
              <CircularProgress size={32} />
              <Typography variant="body2" color="text.secondary">
                Generating your cover image… this can take up to a minute. <Box component="span" sx={{ fontWeight: 'bold' }}>You can continue — it will appear in your photos automatically.</Box>
              </Typography>
            </Box>
          )}
          {genError && (
            <Alert severity="info" sx={{ marginTop: '16px' }}>
              {genError}
            </Alert>
          )}
        </Box>
      )}

      {/* Photo uploader */}
      {provider && provider.id && (
        <Box sx={{ marginBottom: '30px' }}>
          <UploadPhotosTab key={imageRefreshKey} providerId={provider.id} />
        </Box>
      )}

      {/* Bottom navigation */}
      <Box sx={{ display: 'flex', gap: '10px', justifyContent: 'space-between', marginTop: '40px' }}>
        <Button variant="outlined" onClick={onBack}>
          Back
        </Button>
        <Box sx={{ display: 'flex', gap: '10px' }}>
          <Button variant="text" onClick={onSkip}>
            Skip For Now
          </Button>
          <Button variant="contained" onClick={onNext}>
            Save & Continue
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

PhotosStep.propTypes = {
  provider: PropTypes.object,
  generatedImageId: PropTypes.string,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired
};

export default PhotosStep;
